/**
 * Upload New Version Hook
 *
 * Handles uploading a new version of an existing document.
 * Uses generateUploadUrl → upload file to storage → call uploadNewVersion mutation.
 */

import { useState, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useApiMutation } from "@/packages/features-shared";

interface UseUploadNewVersionReturn {
  uploadNewVersion: (file: File) => Promise<void>;
  isUploading: boolean;
}

export function useUploadNewVersion(
  documentId: Id<"documents"> | null,
  options?: {
    onSuccess?: () => void;
    expectedFileType?: string;
  },
): UseUploadNewVersionReturn {
  const [isUploading, setIsUploading] = useState(false);

  const generateUploadUrl = useMutation(
    api.document_mutations.generateUploadUrl,
  );
  const uploadNewVersionMutation = useApiMutation(
    api.document_mutations.uploadNewVersion,
    {
      name: "upload-new-version",
      successMessage: "New version uploaded successfully",
      onSuccess: () => options?.onSuccess?.(),
    },
  );

  const uploadNewVersion = useCallback(
    async (file: File) => {
      if (!documentId) return;
      setIsUploading(true);

      try {
        // Step 1: Get upload URL
        const uploadUrl = await generateUploadUrl({});

        // Step 2: Upload file to Convex storage
        const uploadResult = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });

        if (!uploadResult.ok) {
          throw new Error(`Upload failed: ${uploadResult.statusText}`);
        }

        const { storageId } = (await uploadResult.json()) as {
          storageId: Id<"_storage">;
        };

        // Step 3: Call the uploadNewVersion mutation
        await uploadNewVersionMutation.mutateAsync({
          documentId,
          storageId,
          fileName: file.name,
        });
      } finally {
        setIsUploading(false);
      }
    },
    [documentId, generateUploadUrl, uploadNewVersionMutation],
  );

  return { uploadNewVersion, isUploading };
}
